import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileDown, FileStack } from "lucide-react";
import { type ReactNode, useCallback, useState } from "react";

import { api } from "../api/client";
import { attentionSelector, designateSoon } from "../components/attention";
import { Menu, MenuItem, MenuLabel, MenuSeparator } from "../components/Menu";
import { motion } from "../components/presence";
import { IconButton } from "../components/ui";
import { useTrialExport } from "../licence/trial";
import { type Notice, useUi, type ViewKey } from "../state/ui";
import { briefingKey } from "../views/briefing/briefingJob";
import { countrySheetKey } from "../views/country/countryJob";
import { COUNTRY_WRITE_TARGET, sheetWritten } from "../views/country/countrySheet";
import { k, t } from "../i18n/i18n";
import { Waiting } from "../components/Waiting";
import { genesisBusy, startDocumentGenesis, startDossierGenesis, useGenesis } from "./dossierGenesis";
import { gateNeeded, useCoherence } from "../views/coherence/coherenceState";
import {
  DOSSIER_EXPORT,
  type DossierMissing,
  ORDER_APPROVE,
  STALE_REGENERATE,
  briefingStale,
  countryStale,
  dossierMissing,
  orderNumber,
  orderState,
} from "./dossierState";

export { fetchDossierFile } from "./dossierGenesis";

/** Export du dossier d'exercice en PDF (demandes d'Alexis du 29/09 et du 12/10/2026). Première entrée : le
 *  dossier complet en un seul fichier (fiche pays, mise en ambiance, OPO validé avec ses annexes), bloquée
 *  tant qu'un préalable manque ; son clic ouvre alors la vue du premier préalable et y désigne l'action
 *  attendue (R12), une indication sous l'entrée dit ce qui manque. Dessous, chaque document se télécharge
 *  seul (l'OPO brouillon compris). Menu partagé (R9 : Échap, clic dehors, flèches), rendu à droite de la
 *  barre latérale.
 *
 *  Téléchargement par l'application (constat d'Alexis du 14/10/2026, « le pdf est vide ») : le fichier est
 *  enregistré sous son nom (`downloadPdf`) ; une réponse en erreur, vide ou qui n'est pas un PDF n'est jamais
 *  enregistrée. Genèse visible (demande d'Alexis du 18/10/2026, registre F37) : le menu se referme aussitôt et la
 *  carte de la genèse (DossierGenesisCard) prend le relais — étapes du dossier complet produites par le serveur,
 *  progression, puis « Dossier enregistré » ou l'échec et « Réessayer » ; les documents séparés ont la même carte,
 *  allégée. Un préalable manquant côté serveur (409 `DOSSIER_INCOMPLETE`) ouvre la vue du préalable et y désigne
 *  l'action attendue. Tant qu'une préparation est en cours, les entrées attendent. */

/** Document en cours de préparation. */
export type DossierDownload = "dossier" | "country" | "part" | "oplan" | "order";

/** Indication d'une entrée apparue en fondu (attente de la préparation). */
function FadeIn({ children }: { children: ReactNode }) {
  return <span className={motion("fade", false)}>{children}</span>;
}

/** Préalable manquant : sa vue et l'action qui le lève. */
const MISSING: Record<DossierMissing, { view: ViewKey; target: string; notice: string }> = {
  country: { view: "country", target: COUNTRY_WRITE_TARGET, notice: k("Rédigez d'abord la fiche pays") },
  briefing: { view: "briefing", target: "briefing-write", notice: k("Rédigez d'abord la mise en ambiance") },
  order: { view: "order", target: "order-generate", notice: k("Rédigez d'abord l'OPO") },
  approve: { view: "order", target: ORDER_APPROVE, notice: k("Validez d'abord l'OPO") },
  regenerate_country: {
    view: "country",
    target: STALE_REGENERATE,
    notice: k("Régénérez d'abord les rubriques signalées de la fiche pays : elles ne suivent plus le dossier"),
  },
  regenerate_briefing: {
    view: "briefing",
    target: STALE_REGENERATE,
    notice: k("Régénérez d'abord les paragraphes signalés de la mise en ambiance : ils ne suivent plus le dossier"),
  },
};

/** Préalable manquant : la vue du document s'ouvre et l'action qui le lève y est désignée (l'ordre se
 *  charge : la désignation l'attend un peu plus), avec un avis. */
export function openMissing(
  missing: DossierMissing,
  { setView, notify }: { setView: (view: ViewKey) => void; notify: (notice: Omit<Notice, "id">) => void },
): void {
  const { view, target, notice } = MISSING[missing];
  setView(view);
  designateSoon(attentionSelector(target), target === STALE_REGENERATE ? "ring" : "auto", view === "order" ? 2500 : 1200);
  notify({ text: t(notice), tone: "info" });
}

/** Ce qui manque au dossier complet, dit sous son entrée. */
const MISSING_HINT: Record<DossierMissing, string> = {
  country: k("Rédigez d'abord la fiche pays"),
  briefing: k("Rédigez d'abord la mise en ambiance"),
  order: k("Rédigez d'abord l'OPO"),
  approve: k("Validez d'abord l'OPO"),
  regenerate_country: k("Régénérez d'abord les rubriques signalées de la fiche pays"),
  regenerate_briefing: k("Régénérez d'abord les paragraphes signalés de la mise en ambiance"),
};

/** Entrée « Dossier complet (PDF) » : bloquée tant qu'un préalable manque (`onMissing` le désigne), en
 *  attente pendant la lecture de la fiche pays et de la mise en ambiance. */
export function CompleteDossierItem({
  missing,
  checking,
  preparing = false,
  busy = false,
  onDownload,
  onMissing,
  corps = false,
}: {
  missing: DossierMissing | null;
  /** Dossier de corps d'armée : l'OPLAN du corps en fait partie (docs/08 §25). */
  corps?: boolean;
  checking: boolean;
  /** Dossier en cours de préparation (requête partie) ; `busy` : un autre document du menu l'est. */
  preparing?: boolean;
  busy?: boolean;
  onDownload: () => void;
  onMissing: (missing: DossierMissing) => void;
}) {
  return (
    <MenuItem
      icon={FileStack}
      label={t("Dossier complet (PDF)")}
      hint={
        preparing ? (
          <FadeIn><Waiting>{t("Préparation du dossier…")}</Waiting></FadeIn>
        ) : checking ? (
          <Waiting>{t("Vérification…")}</Waiting>
        ) : missing ? (
          t(MISSING_HINT[missing])
        ) : corps ? (
          t("Fiche pays, mise en ambiance, OPLAN et OPO validé, en un seul fichier")
        ) : (
          t("Fiche pays, mise en ambiance et OPO validé, en un seul fichier")
        )
      }
      disabled={checking || preparing || busy}
      blocked={missing !== null}
      onBlocked={() => missing && onMissing(missing)}
      onSelect={onDownload}
      keepOpen
    />
  );
}

export function DossierExportMenu() {
  const exerciseId = useUi((s) => s.exerciseId);
  const setView = useUi((s) => s.setView);
  const notify = useUi((s) => s.notify);
  const mapOpo = useUi((s) => s.opo);
  const [open, setOpen] = useState(false);
  const trial = useTrialExport();
  const genesis = useGenesis((s) => s.genesis);
  const pending: DossierDownload | null = genesisBusy(genesis) && genesis ? genesis.kind : null;
  const queryClient = useQueryClient();
  const close = useCallback(() => setOpen(false), []);
  const { data: exercises } = useQuery({
    queryKey: ["exercises"],
    queryFn: api.exercises,
  });
  const exercise = exercises?.find((e) => e.id === exerciseId);
  const opo = orderNumber(exercise, mapOpo);
  // Partie I et fiche pays rédigées ? Lues seulement à l'ouverture du menu (même cache que les vues).
  const briefing = useQuery({
    queryKey: briefingKey(exerciseId),
    queryFn: () => api.briefing(exerciseId as string),
    enabled: open && Boolean(exerciseId),
    retry: false,
  });
  const country = useQuery({
    queryKey: countrySheetKey(exerciseId),
    queryFn: () => api.countrySheet(exerciseId as string),
    enabled: open && Boolean(exerciseId),
    retry: false,
  });
  if (!exerciseId) return null;
  const partOne = Boolean(briefing.data?.sections?.some((s) => s.text));
  const countryReady = sheetWritten(country.data);
  const order = orderState(exercise, opo);
  const hasOrder = order !== "none";
  const missing = dossierMissing(countryReady, partOne, order, {
    country: countryStale(country.data?.sections),
    briefing: briefingStale(briefing.data?.sections),
  });
  const partUrl = api.briefingExportUrl(exerciseId, "pdf");
  const countryUrl = api.countrySheetExportUrl(exerciseId, "pdf");
  const orderUrl = `/api/exercises/${exerciseId}/order/export/pdf?number=${opo}`;
  const dossierUrl = api.dossierExportUrl(exerciseId, opo);
  const centac = exercise?.parameters.level === "CENTAC";
  // Corps d'armée (docs/08 §25) : l'OPLAN du corps, juste avant l'OPO.
  const corps = exercise?.parameters.level === "CORPS";
  const oplanState = exercise?.orders?.oplan;
  const oplanUrl = `/api/exercises/${exerciseId}/order/export/pdf?number=0`;
  // Lecture de la Partie I ou de la fiche pays en cours : requête en cours, l'entrée attend (R12 : `disabled`
  // pendant une requête).
  const checkingPart = briefing.isLoading;
  const checkingCountry = country.isLoading;

  // Préalable manquant : le menu se referme, la vue du document s'ouvre et l'action attendue y est désignée.
  const designateMissing = (missing: DossierMissing) => {
    close();
    openMissing(missing, { setView, notify });
  };
  const checking = checkingCountry || checkingPart;
  // Téléchargement : un seul à la fois ; le menu se referme, la carte de la genèse prend le relais.
  const download = async (key: DossierDownload, url: string, checked = false) => {
    if (pending) return;
    close();
    // Dossier complet : la cohérence tactique (docs/24) est relue d'abord ; des points restent ouverts ? le panneau
    // s'ouvre avec un avertissement et « Télécharger quand même » — jamais de blocage.
    if (key === "dossier" && !checked) {
      const coherence = await api.coherence(exerciseId, opo).catch(() => null);
      if (gateNeeded(coherence)) {
        useCoherence.getState().open({ kind: "download", run: () => void download(key, url, true) });
        setView("coherence");
        return;
      }
    }
    const name = exercise?.name ?? "";
    const { missing } =
      key === "dossier" ? await startDossierGenesis(exerciseId, opo, name) : await startDocumentGenesis(key, exerciseId, url, name);
    if (missing) {
      // L'état affiché était en retard sur le serveur (OPO repassé en rédaction…) : relu, puis désigné.
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      openMissing(missing, { setView, notify });
    }
  };
  const items = [
    {
      key: "country",
      label: t("Fiche pays (PDF)"),
      hint: pending === "country"
        ? <FadeIn><Waiting>{t("Préparation du document…")}</Waiting></FadeIn>
        : countryReady
        ? t("Le pays de l'ennemi joué, carte comprise")
        : checkingCountry
          ? <Waiting>{t("Vérification…")}</Waiting>
          : t("Rédigez d'abord la fiche pays"),
      missing: countryReady ? null : ("country" as const),
      disabled: checkingCountry,
      run: () => void download("country", countryUrl),
    },
    {
      key: "part",
      label: t("Mise en ambiance (PDF)"),
      hint: pending === "part"
        ? <FadeIn><Waiting>{t("Préparation du document…")}</Waiting></FadeIn>
        : partOne
        ? t("Partie I : préambule et mise en ambiance")
        : checkingPart
          ? <Waiting>{t("Vérification…")}</Waiting>
          : t("Rédigez d'abord la mise en ambiance"),
      missing: partOne ? null : ("briefing" as const),
      disabled: checkingPart,
      run: () => void download("part", partUrl),
    },
    ...(corps
      ? [
          {
            key: "oplan",
            label: t("OPLAN (PDF)"),
            hint: pending === "oplan"
              ? <FadeIn><Waiting>{t("Préparation du document…")}</Waiting></FadeIn>
              : !oplanState
              ? t("Rédigez d'abord l'OPO : l'OPLAN est rédigé avec lui")
              : oplanState === "finalized"
                ? t("Le plan d'opération validé, annexes comprises")
                : t("Le plan d'opération en brouillon, annexes comprises"),
            missing: oplanState ? null : ("order" as const),
            disabled: false,
            run: () => void download("oplan", oplanUrl),
          },
        ]
      : []),
    {
      key: "order",
      label: centac ? t("OPO n° {n} (PDF)", { n: opo }) : t("OPO (PDF)"),
      hint: pending === "order"
        ? <FadeIn><Waiting>{t("Préparation du document…")}</Waiting></FadeIn>
        : !hasOrder
        ? t("Rédigez d'abord l'OPO")
        : order === "approved"
          ? t("L'ordre d'opérations validé, annexes comprises")
          : t("L'ordre d'opérations en brouillon, annexes comprises"),
      missing: hasOrder ? null : ("order" as const),
      disabled: false,
      run: () => void download("order", orderUrl),
    },
  ];

  return (
    <Menu
      open={open}
      onClose={close}
      portal
      side="right"
      width={270}
      ariaLabel={t("Exporter le dossier d'exercice")}
      trigger={
        <IconButton
          size="xs"
          data-attention={DOSSIER_EXPORT}
          onClick={() => setOpen((o) => !o)}
          // Licence d'essai (registre F44) : grisé, l'appui le dit (avis), le survol aussi.
          blocked={trial.blocked}
          onBlocked={trial.onBlocked}
          title={trial.title ?? t("Exporter le dossier d'exercice en PDF")}
          aria-label={t("Exporter le dossier d'exercice en PDF")}
        >
          <Download size={14} />
        </IconButton>
      }
    >
      <MenuLabel>{t("Exporter le dossier d'exercice")}</MenuLabel>
      <CompleteDossierItem
        missing={missing}
        checking={checking}
        preparing={pending === "dossier"}
        busy={pending !== null && pending !== "dossier"}
        onDownload={() => void download("dossier", dossierUrl)}
        onMissing={designateMissing}
        corps={corps}
      />
      <MenuSeparator />
      <MenuLabel>{t("Documents séparés")}</MenuLabel>
      {items.map((item) => (
        <MenuItem
          key={item.key}
          icon={FileDown}
          label={item.label}
          hint={item.hint}
          disabled={item.disabled || pending !== null}
          blocked={item.missing !== null}
          onBlocked={() => item.missing && designateMissing(item.missing)}
          onSelect={item.run}
          keepOpen
        />
      ))}
    </Menu>
  );
}
